/**
 * Care-Plan-driven referrals tab.
 *
 * Lists every service_referrals row where member_request_type =
 * 'workflow_triggered' and request_origin_metadata.source_module =
 * 'care_plan_workflow' — i.e., referrals materialized by the care-plan
 * referral orchestrator from a care_plan_interventions row.
 *
 * Today there is no dedicated list endpoint; we fetch `/api/referrals` and
 * filter client-side. When the referral list endpoint gains origin filters
 * (planned), swap to a server-side filter for pagination.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '../../lib/authFetch';
import { BookOpen, RefreshCw } from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface ReferralRow {
  id: number;
  cbo_id: number;
  member_id: number | null;
  status: string;
  source_type: string;
  service_category_code: string | null;
  service_type_code: string | null;
  urgency_level: string | null;
  created_at: string | null;
  member_request_type?: string | null;
  // Pydantic emits this as an object; older deployments may emit a JSON
  // string. parseOrigin() below handles both.
  request_origin_metadata?: any;
}

const CATEGORY_COLOR: Record<string, string> = {
  NEMT:              'bg-amber-100  text-amber-800',
  HOUSING:           'bg-sky-100    text-sky-800',
  FOOD:              'bg-lime-100   text-lime-800',
  CHW:               'bg-indigo-100 text-indigo-800',
  ECM_OUTREACH:      'bg-purple-100 text-purple-800',
  BEHAVIORAL_HEALTH: 'bg-fuchsia-100 text-fuchsia-800',
  EMPLOYMENT:        'bg-teal-100   text-teal-800',
};

const STATUS_COLOR: Record<string, string> = {
  created:           'bg-gray-100  text-gray-700',
  matching:          'bg-blue-100  text-blue-700',
  matched:           'bg-indigo-100 text-indigo-700',
  service_started:   'bg-emerald-100 text-emerald-700',
  service_completed: 'bg-green-100 text-green-700',
  closed:            'bg-gray-100  text-gray-500',
};

export const CarePlanDrivenReferrals: React.FC<{
  cboId: number;
  onOpenReferral?: (referralId: number) => void;
}> = ({ cboId, onOpenReferral }) => {
  const tabbed = useTabbedChrome();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ReferralRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reorchestrating, setReorchestrating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // /api/service-referrals returns the new schema (member_request_type,
      // request_origin_metadata, service_category_code) — /api/referrals
      // queries the legacy referrals table and lacks the workflow-origin
      // metadata, so the Care-Plan-driven filter never matches.
      const data = await authFetchJson<ReferralRow[]>(`/api/service-referrals?limit=200&include_closed=true`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [cboId]);

  // Helper: request_origin_metadata may arrive as object (Pydantic Dict)
  // OR as a JSON string in older serializer paths. Normalize either shape.
  const parseOrigin = (raw: any): Record<string, any> => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return {}; }
    }
    return {};
  };

  // Filter: a referral is "care-plan driven" if EITHER
  //   member_request_type='workflow_triggered' AND metadata.source_module='care_plan_workflow'
  // OR source_type='care_plan_goal' (the source_type the bridge writes
  // when materializing a referral from a care_plan_intervention; this
  // catches rows where the metadata column never got populated, e.g.
  // pre-trigger-dispatcher seed data).
  const carePlanReferrals = useMemo(
    () => rows.filter(r => {
      const meta = parseOrigin(r.request_origin_metadata);
      const workflowDriven =
        r.member_request_type === 'workflow_triggered' &&
        meta.source_module === 'care_plan_workflow';
      const fromCarePlanGoal = r.source_type === 'care_plan_goal';
      return workflowDriven || fromCarePlanGoal;
    }),
    [rows]
  );

  const reorchestrate = async () => {
    setReorchestrating(true);
    try {
      await authFetchJson(
        `/api/engagement-intelligence/orgs/${cboId}/care-plan-interventions/backfill-referrals`,
        { method: 'POST' }
      );
      setRefreshing(true);
      await load();
    } finally {
      setReorchestrating(false);
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          {!tabbed && (
          <BookOpen className="h-4 w-4 text-indigo-600" />
          )}
          <div>
            {!tabbed && (
            <h2 className="text-base font-semibold text-gray-900">Care-Plan-driven referrals</h2>
            )}
            <p className="text-xs text-gray-500">
              Service referrals materialized from care_plan_interventions through the orchestrator workflow.
            </p>
          </div>
        </div>
        <button
          onClick={reorchestrate}
          disabled={reorchestrating}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${reorchestrating ? 'animate-spin' : ''}`} />
          {reorchestrating ? 'Re-orchestrating…' : 'Re-orchestrate'}
        </button>
      </div>

      {error && (
        <div className="m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      <div className="flex-1 overflow-auto">
        {loading || refreshing ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : carePlanReferrals.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            <p>{t('referrals.noCarePlanDriven', 'No referrals from a care plan yet.')}</p>
            {/* The empty state has to say which empty it is. This screen shows
                ONLY referrals a care plan produced; a referral from a partner
                or entered by hand is not missing, it is one tab over. Without
                that sentence an empty screen reads as a fault, which is exactly
                how it was read. */}
            <p className="mt-2 text-xs">
              {rows.length > 0
                ? t(
                    'referrals.carePlanEmptyButOthers',
                    'This member has {{count}} other referrals — from a partner, or entered by hand. Those are under Referrals.',
                    { count: rows.length },
                  )
                : t('referrals.carePlanEmptyNoOthers', 'This member has no referrals recorded at all.')}
            </p>
            {/* The diagnostic that used to live here named internal endpoints
                and column values, told a case worker to run the orchestrator,
                and printed a raw referral row — the member's own name included
                — into the page. None of that belongs on a care-worker screen,
                so it is dev-only now. */}
            {import.meta.env.DEV && rows.length > 0 && (
              <details className="mx-auto mt-3 max-w-md text-left text-xs text-gray-400">
                <summary className="cursor-pointer">Debug: first row</summary>
                <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2">
                  {JSON.stringify(rows[0], null, 2).slice(0, 600)}
                </pre>
              </details>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Member</th>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">Intervention</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Urgency</th>
                <th className="px-4 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {carePlanReferrals.map(r => {
                const category = r.request_origin_metadata?.category || r.service_category_code || '—';
                const catColor = CATEGORY_COLOR[category] || 'bg-gray-100 text-gray-700';
                const statusColor = STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-700';
                return (
                  <tr
                    key={r.id}
                    onClick={() => onOpenReferral?.(r.id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">{r.id}</td>
                    <td className="px-4 py-2 text-gray-900">{r.member_id ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${catColor}`}>
                        {category}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {r.request_origin_metadata?.intervention_title || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{r.urgency_level}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CarePlanDrivenReferrals;
